import useDebouncedCallback from 'client/widget-components/customHooks/useDebouncedCallback';
import { ChangeEvent, MouseEvent, useCallback, useRef, useState } from 'react';
import { getWindowInBrowser } from '../../../../RuntimeWindow';
import { POINTS } from '../types';
import { RangeInputsProps } from './RangeSliderInputs';

function useRangeSliderLogic({
    rangeValues,
    minRange = 1,
    ...props
}: RangeInputsProps) {
    const [activeSlider, setActiveSlider] = useState<POINTS | null>(null);
    const [mouseIsDown, setMouseIsDown] = useState(false);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const trackDimensionsRef = useRef<DOMRect | null>(null);
    const sliderDirectionRef = useRef('ltr');

    const trackRefCB = useCallback((node: HTMLDivElement) => {
        if (node) {
            trackRef.current = node;
        }
    }, []);

    const setupTrackProperties = () => {
        if (trackRef.current) {
            trackDimensionsRef.current =
                trackRef.current.getBoundingClientRect();
            const computedStyle = getWindowInBrowser().getComputedStyle(
                trackRef.current
            );
            sliderDirectionRef.current =
                computedStyle.getPropertyValue('direction');
        }
    };

    const getSliderValue = useCallback((event: MouseEvent<HTMLElement>) => {
        if (trackDimensionsRef.current) {
            const {
                width: trackWidth,
                left,
                right,
            } = trackDimensionsRef.current;
            const clickPos =
                sliderDirectionRef.current === 'rtl'
                    ? right - event.clientX
                    : event.clientX - left;

            const valueRange = props.max - props.min;
            return Math.round((clickPos / trackWidth) * valueRange + props.min);
        }
        return null;
    }, []);

    const { start: startValue, end: endValue } = rangeValues;

    const changeStartValue = (newStartValue: number) => {
        const startValueBelowEnd = Math.min(newStartValue, endValue - minRange);
        const startValueAboveMin = Math.max(startValueBelowEnd, props.min);
        props.onChange({ start: startValueAboveMin, end: endValue });
    };
    const changeEndValue = (newEndValue: number) => {
        const endValueAboveStart = Math.max(newEndValue, startValue + minRange);
        const endValueBelowMax = Math.min(endValueAboveStart, props.max);
        props.onChange({ start: startValue, end: endValueBelowMax });
    };

    const handleActiveTrack = (event: MouseEvent<HTMLElement>) => {
        const newSliderValue = getSliderValue(event);
        if (newSliderValue) {
            const isCloserToStart =
                Math.abs(rangeValues.start - newSliderValue) <
                Math.abs(rangeValues.end - newSliderValue);

            const relativeSlider = isCloserToStart ? POINTS.START : POINTS.END;
            if (activeSlider !== relativeSlider) {
                setActiveSlider(relativeSlider);
            }
        }
    };

    const onMouseMove = useDebouncedCallback({
        callback: handleActiveTrack,
        msToWait: 10,
    });

    return {
        trackRefCB,
        activeSlider,
        wrapperEvents: {
            onMouseMove: (e: MouseEvent<HTMLDivElement>) => {
                !mouseIsDown && onMouseMove(e);
            },
            onMouseDown: () => {
                setupTrackProperties();
                setMouseIsDown(true);
            },
            onMouseUp: () => {
                setActiveSlider(null);
                setMouseIsDown(false);
            },
        },
        onStartInputChange: (event: ChangeEvent<HTMLInputElement>) => {
            changeStartValue(+event.target.value);
        },
        onEndInputChange: (event: ChangeEvent<HTMLInputElement>) => {
            changeEndValue(+event.target.value);
        },
    };
}

export default useRangeSliderLogic;
